import axios from "axios";
import i18next from "i18next";
import { toast } from "components/FluentToast";

// NOTE: this module is imported by store/util.ts (handleAxiosError), so it must not
// import from store/api/api.ts - that would create an import cycle (api.ts imports
// handleAxiosError from store/util.ts). The connect-url call is made directly here.
const url = process.env.REACT_APP_BASE_URL;

const TOGETHER_RETURN_PATH_KEY = "together.returnPath";
const TOGETHER_AUTO_CONNECT_AT_KEY = "together.autoConnectAt";
const TOGETHER_PENDING_FLOTTOMAT_KEY = "together.pendingFlottomat";
const AUTO_CONNECT_MIN_INTERVAL_MS = 2 * 60 * 1000;

export type PendingFlottomat = { fleetId: number; vehicleId: number };

/**
 * Remembers the Flottomat action the user triggered, so it can be resumed after the
 * TOGETHER consent redirect round-trip (which reloads the SPA and clears redux state).
 */
export const setPendingFlottomat = (fleetId: number, vehicleId: number) => {
  sessionStorage.setItem(
    TOGETHER_PENDING_FLOTTOMAT_KEY,
    JSON.stringify({ fleetId, vehicleId }),
  );
};

export const consumePendingFlottomat = (): PendingFlottomat | null => {
  const raw = sessionStorage.getItem(TOGETHER_PENDING_FLOTTOMAT_KEY);
  sessionStorage.removeItem(TOGETHER_PENDING_FLOTTOMAT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingFlottomat;
  } catch {
    return null;
  }
};

export const TOGETHER_NOT_CONNECTED_CODE = "bfm.fms.TogetherNotConnected.txt";

/**
 * Starts the one-time TOGETHER (TIS) consent flow: remembers the current page and
 * redirects the browser to the TOGETHER authorization page. With the AAD federation
 * on the TOGETHER side this is a silent redirect round-trip - the user is
 * authenticated via their existing Azure AD session and returned to
 * /together/callback without seeing a TOGETHER login page.
 */
export const startTogetherConnect = async () => {
  const res = await axios.get<{ url: string }>(`${url}/TogetherConnectUrl`);
  sessionStorage.setItem(
    TOGETHER_RETURN_PATH_KEY,
    window.location.pathname + window.location.search,
  );
  window.location.href = res.data.url;
};

export const getTogetherReturnPath = () =>
  sessionStorage.getItem(TOGETHER_RETURN_PATH_KEY) || "/";

export const clearTogetherReturnPath = () =>
  sessionStorage.removeItem(TOGETHER_RETURN_PATH_KEY);

/**
 * True when a backend call failed only because the user has no (valid) TOGETHER
 * connection (BFM error code bfm.fms.TogetherNotConnected.txt).
 */
export const isTogetherNotConnectedError = (err: any): boolean => {
  const data = err?.response?.data;
  if (!data) return false;
  if (Array.isArray(data)) {
    return data.some((e: any) => e?.messageCode === TOGETHER_NOT_CONNECTED_CODE);
  }
  return data.messageCode === TOGETHER_NOT_CONNECTED_CODE;
};

/**
 * If the error is a missing/expired TOGETHER connection, silently starts the
 * consent flow (federated - at most a page reload for the user) and returns true
 * so the caller can skip its error toast. A sessionStorage guard prevents a
 * redirect loop when a just-completed connect attempt did not fix the problem
 * (e.g. the user is not provisioned on TIS) - in that case the normal error
 * handling takes over.
 */
export const tryAutoTogetherConnect = (err: any): boolean => {
  if (!isTogetherNotConnectedError(err)) return false;

  const lastAttempt = Number(
    sessionStorage.getItem(TOGETHER_AUTO_CONNECT_AT_KEY) || 0,
  );
  if (Date.now() - lastAttempt < AUTO_CONNECT_MIN_INTERVAL_MS) {
    return false;
  }
  sessionStorage.setItem(TOGETHER_AUTO_CONNECT_AT_KEY, String(Date.now()));

  startTogetherConnect().catch(() => {
    toast.error(
      i18next.t("bfm.together.connectFailed", "Connecting to TOGETHER failed."),
    );
  });
  return true;
};
